import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Sidebarnew from "./NewUI/Sidebarnew";
import Navigationnew from "./NewUI/Navigationnew";
import api from "../api/axios";
import "./Home.css";
import { toast } from "react-toastify";

function Home() {
  const navigate = useNavigate();
  const location = useLocation();

const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("homeActiveTab") || "My Agents");
  const [customAgents, setCustomAgents] = useState([]);
  const [predefinedAgents, setPredefinedAgents] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({ canWriteAgent: false });

  const [selectedDept, setSelectedDept] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [hideDefaults, setHideDefaults] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);

  // Auto-filter from navigation state
  useEffect(() => {
    if (location.state?.autoFilter) {
      setActiveTab(location.state.tab || "My Agents");
      setSelectedDept(location.state.dept || "All");
      setSelectedType(location.state.typeName || "All");
      setSelectedStatus("All");
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Initial data fetch
  useEffect(() => {
    fetchUserPermissions();
    fetchPredefinedAgents();
    fetchCustomAgents();
  }, []);

  const fetchUserPermissions = async () => {
    try {
      const res = await api.get("/user-roles/me");
      const role = res.data;
      if (role.isAdmin) {
        setPermissions({ canWriteAgent: true });
        return;
      }
      if (role && !role.noRoleDefined) {
        let hasAgentWrite = false;
        if (role.permissions?.agent) {
          hasAgentWrite = role.permissions.agent.write === true;
        } else {
          hasAgentWrite =
            (role.write_access || "").includes("Agent") ||
            (role.both_access || "").includes("Agent");
        }
        setPermissions({ canWriteAgent: hasAgentWrite });
      } else {
        setPermissions({ canWriteAgent: true });
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setPermissions({ canWriteAgent: true });
    }
  };

  const fetchPredefinedAgents = () => {
    api
      .get("/predefined-agents")
      .then((res) => {
        const templates = Array.isArray(res.data) ? res.data : [];
        setPredefinedAgents(templates);
        const uniqueDepts = [...new Set(templates.map((a) => a.category))].filter(Boolean);
        setAvailableDepartments(uniqueDepts);
      })
      .catch((err) => console.error("Error fetching templates:", err));
  };

  const fetchCustomAgents = () => {
    setLoading(true);
    api
      .get("/agents")
      .then((res) => {
        setCustomAgents(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching custom agents:", err);
        setLoading(false);
      });
  };

  const handleDeptChange = (e) => {
    setSelectedDept(e.target.value);
    setSelectedType("All");
  };

  const getAvailableTypes = () => {
    const types = new Set();
    customAgents.forEach((a) => {
      const parent = predefinedAgents.find(
        (p) =>
          p.id === a.template_id ||
          (p.name && a.type && String(p.name).toLowerCase() === String(a.type).toLowerCase())
      );
      const matchDept =
        selectedDept === "All" || (parent && parent.category === selectedDept);
      if (matchDept && a.type) {
        types.add(a.type);
      }
    });
    return [...types].filter(Boolean);
  };

  const getByCategory = (categoryName) => {
    return predefinedAgents.filter((agent) => {
      const matchesSearch = String(agent.name || "")
        .toLowerCase()
        .includes(String(searchQuery || "").toLowerCase());
      const matchesType =
        selectedType === "All" ||
        agent.name === selectedType ||
        agent.type === selectedType;
      return matchesSearch && agent.category === categoryName && matchesType;
    });
  };

  const getCustomByCategory = (categoryKeyword) => {
    return customAgents.filter((agent) => {
      const matchesSearch = String(agent.name || "")
        .toLowerCase()
        .includes(String(searchQuery || "").toLowerCase());
      if (!matchesSearch) return false;
      const isDraft =
        agent.status === "Draft" ||
        !agent.doc_count ||
        parseInt(agent.doc_count) === 0;
      if (selectedStatus === "Active" && isDraft) return false;
      if (selectedStatus === "Draft" && !isDraft) return false;
      const parentTemplate = predefinedAgents.find(
        (p) =>
          p.id === agent.template_id ||
          (p.name && agent.type && String(p.name).toLowerCase() === String(agent.type).toLowerCase())
      );
      if (selectedType !== "All") {
        const matchesAgentType = agent.type === selectedType;
        const matchesParentType = parentTemplate && parentTemplate.name === selectedType;
        if (!matchesAgentType && !matchesParentType) return false;
      }
      if (categoryKeyword === "Custom") {
        return !parentTemplate;
      }
      return parentTemplate && parentTemplate.category === categoryKeyword;
    });
  };

  const handleDeleteAgent = (agentId, agentName) => {
    setAgentToDelete({ id: agentId, name: agentName });
    setDeleteModalVisible(true);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;
    try {
      await api.delete(`/agents/${agentToDelete.id}`);
      setCustomAgents((prev) => prev.filter((a) => a.id !== agentToDelete.id));
      toast.success("Agent deleted successfully");
      setDeleteModalVisible(false);
      setAgentToDelete(null);
    } catch (err) {
      toast.error(
        "Failed to delete agent: " +
          (err.response?.data?.message || err.message || "Unknown error")
      );
      setDeleteModalVisible(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setAgentToDelete(null);
  };

  const hasVisibleAgents = () => {
    if (selectedDept === "All" && getCustomByCategory("Custom").length > 0) return true;
    if (!hideDefaults) {
      return availableDepartments.some((dept) => {
        if (selectedDept !== "All" && selectedDept !== dept) return false;
        return getCustomByCategory(dept).length > 0;
      });
    }
    return false;
  };

  return (
    <div className="db-page-layout">

      {/* ── SIDEBAR ── */}
      <aside className="db-sidebar-shell">
        <Sidebarnew />
      </aside>

      {/* ── MAIN WRAPPER ── */}
      <div className="db-main-wrapper">

        {/* ── TOP NAV ── */}
       <div className="db-topbar-shell">
  <Navigationnew
    setArtifactsOpen={() => {}}
    artifactsOpen={false}
  />
</div>

        {/* ── PAGE CONTENT ── */}
        <div className="home-page-content">

          {/* ==================== UPDATED FILTER BAR ==================== */}
          <div className="home-filter-bar">

            {/* ── PAGE HEADER ROW ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 18px 0' }}>
              <div>
                <h1 style={{ fontSize: '19px', fontWeight: '530', color: '#111827', margin: 0 }}>Home</h1>
                <p style={{ fontSize: '13px', color: '#6B7280', margin: '2px 0 0' }}>Pre-configured AI agents for specific business functions.</p>
              </div>
              {permissions.canWriteAgent && activeTab === "My Agents" && (
                <button className="btn filter-action-btn-primary compact-create-btn" onClick={() => navigate("/create-agent")}>
                  + Create Agent
                </button>
              )}
            </div>

            {/* ── TABS ROW ── */}
            <div style={{ padding: '16px 18px 0' }}>
             <div className="home-filter-tabs-pill">
                <button className={`home-tab-btn ${activeTab === "My Agents" ? "active" : ""}`} onClick={() => { setActiveTab("My Agents"); sessionStorage.setItem("homeActiveTab", "My Agents"); }}>My Agents</button>
                <button className={`home-tab-btn ${activeTab === "Templates" ? "active" : ""}`} onClick={() => { setActiveTab("Templates"); sessionStorage.setItem("homeActiveTab", "Templates"); }}>Templates</button>
              </div>
            </div>

            {/* ── SEARCH + FILTERS ROW ── */}
            <div style={{ padding: '12px 18px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="home-filter-search" style={{ width: '260px', flexShrink: 0 }}>
                <input 
                  type="search" 
                  className="form-control" 
                  placeholder="Search agents..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                />
              </div>
              <select className="form-select filter-select-dropdown" style={{ width: '145px', flexShrink: 0 }} value={selectedDept} onChange={handleDeptChange}>
                <option value="All">All Departments</option>
                {availableDepartments.map((dept, i) => <option key={i} value={dept}>{dept}</option>)}
              </select>
              {activeTab === "My Agents" && (
                <select className="form-select filter-select-dropdown" style={{ width: '145px', flexShrink: 0 }} value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                </select>
              )}
            </div>

          </div>

          {/* Main Content */}
          <div className="home-sections">
            {activeTab === "My Agents" ? (
              <>
                {selectedDept === "All" && (
                  <HomeSection
                    title="Custom Agents"
                    agents={getCustomByCategory("Custom")}
                    isCustom={true}
                    loading={loading}
                    canWrite={permissions.canWriteAgent}
                    onDelete={handleDeleteAgent}
                    predefinedAgents={predefinedAgents}
                  />
                )}

                {!hideDefaults &&
                  availableDepartments.map((dept, i) => {
                    if (selectedDept !== "All" && selectedDept !== dept) return null;
                    return (
                      <HomeSection
                        key={i}
                        title={dept}
                        agents={getCustomByCategory(dept)}
                        isCustom={true}
                        loading={loading}
                        canWrite={permissions.canWriteAgent}
                        onDelete={handleDeleteAgent}
                        predefinedAgents={predefinedAgents}
                      />
                    );
                  })}

                {!loading && !hasVisibleAgents() && (
                  <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-5 text-center">
                    <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.4 }}>🤖</div>
                    <h6 style={{ color: "#6b7280", fontWeight: 600, marginBottom: "8px" }}>
                      No agents available yet
                    </h6>
                    <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
                      {permissions.canWriteAgent
                        ? "Create your first agent or explore Templates to get started."
                        : "Your administrator hasn't assigned any agents to your account yet."}
                    </p>
                    {permissions.canWriteAgent && (
                      <button
                        className="btn filter-action-btn-primary mt-4 px-4 py-2"
                        onClick={() => navigate("/create-agent")}
                      >
                        + Create Agent
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {availableDepartments.map((dept, i) => {
                  if (selectedDept !== "All" && selectedDept !== dept) return null;
                  return (
                    <HomeSection
                      key={i}
                      title={dept}
                      agents={getByCategory(dept)}
                      isCustom={false}
                      canWrite={permissions.canWriteAgent}
                      predefinedAgents={predefinedAgents}
                    />
                  );
                })}
              </>
            )}
          </div>

        </div>

        {/* Delete Confirmation Modal */}
        {deleteModalVisible && (
          <div className="custom-delete-overlay" onClick={cancelDelete}>
            <div className="custom-delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="custom-delete-header">
                <div className="delete-icon-circle">
                  <img src="/images/binn.svg" alt="warning" />
                </div>
                <h5>Delete Agent?</h5>
              </div>
              <div className="custom-delete-body">
                <p>
                  Are you sure you want to delete <strong>"{agentToDelete?.name}"</strong>?
                </p>
                <p className="delete-warning">This action is permanent and cannot be undone.</p>
              </div>
              <div className="custom-delete-footer">
                <button className="btn-cancel-delete" onClick={cancelDelete}>Cancel</button>
                <button className="btn-confirm-delete" onClick={confirmDeleteAgent}>Yes, Delete</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ====================== SUB COMPONENTS ====================== */

const getAvatarInfo = (title) => {
  if (title === "Custom Agents") return { letter: "C", bg: "#EDE9FE", color: "#7C3AED" };
  const t = title.toLowerCase();
  if (t.includes("hr")) return { letter: "H", bg: "#FEF3C7", color: "#D97706" };
  if (t.includes("finance")) return { letter: "F", bg: "#D1FAE5", color: "#059669" };
  if (t.includes("legal")) return { letter: "L", bg: "#DBEAFE", color: "#2563EB" };
  return { letter: title.charAt(0).toUpperCase(), bg: "#DBEAFE", color: "#2563EB" };
};

function HomeSection({ title, agents, isCustom, loading, canWrite, onDelete, predefinedAgents }) {
  if (!loading && (!agents || agents.length === 0)) return null;
  const avatarInfo = getAvatarInfo(title);
  return (
    <section className="home-section mb-2">
      <h6 className="home-section__title">{title}</h6>
      <hr className="mt-1 mb-2" style={{ borderTop: "1px solid #e0e0e0" }} />
      <div className="row g-3 home-section__grid">
        {loading ? (
          <p className="ps-3 text-muted">Loading agents...</p>
        ) : (
          agents.map((agent) => (
            <RenderCard
              key={agent.id || agent.name}
              title={agent.name}
              desc={agent.description}
              tags={[]}
              keyId={agent.id}
              showCount={!isCustom}
              isAdded={agent.is_added}
              canWrite={canWrite}
              agentCount={parseInt(agent.real_agent_count) || 0}
              agentData={agent}
              onDelete={onDelete}
              avatarInfo={avatarInfo}
              predefinedAgents={predefinedAgents}
            />
          ))
        )}
      </div>
    </section>
  );
}

const getCardIcon = (category, isDraft) => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("hr")) return "/images/newhr.svg";
  if (cat.includes("finance")) return "/images/newfi.svg";
  if (cat.includes("legal")) return "/images/newfi.svg";
  return isDraft ? "/images/newcusdraft.svg" : "/images/newcusact.svg";
};
// Add this helper near getCardIcon
const getIconBg = (category) => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("hr")) return "#FEF3C7";      // warm yellow
  if (cat.includes("finance")) return "#D1FAE5";  // green
  if (cat.includes("legal")) return "#DBEAFE";    // blue
  return "#EFF4FF";                                // default blue-grey
};

function RenderCard({
  title, desc, tags, keyId, showCount, isAdded, canWrite,
  agentCount, agentData, onDelete, avatarInfo, predefinedAgents,
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isTemplateCard = showCount === true;
  const isDraft =
    agentData.status === "Draft" ||
    !agentData.doc_count ||
    parseInt(agentData.doc_count) === 0;

 const handleCardClick = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    if (isTemplateCard) { handleTemplateCountClick(e); return; }
    // Active agents → go to chat on card body click
    if (!isDraft) {
      if (agentData.type === "RFP Creator Agent") {
        navigate(`/chat-bot?agentId=${agentData.id}`);
      } else {
        navigate(`/ChatInterface/${agentData.id}`);
      }
      return;
    }
    // Draft agents → go to configure/screening
    if (agentData.user_can_edit === false) {
      toast.warning("You only have View access to this agent.");
      return;
    }
    navigate("/ScreeningAgent", { state: { existingAgent: agentData } });
  };

  const handleTemplateCountClick = (e) => {
    e.preventDefault();
    navigate("/home", {
      replace: true,
      state: { autoFilter: true, tab: "My Agents", dept: agentData.category || "All", typeName: title },
    });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete(agentData.id, title);
  };

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent card's onClick from firing
    if (agentData.user_can_edit === false) {
      alert("You only have View access to this agent.");
      return;
    }
    navigate("/ScreeningAgent", {
      state: { existingAgent: agentData },
    });
  };

  return (
    <div className="col-12 col-sm-6 col-md-3 col-lg-3 home-card-col">
      <div className="card shadow-sm home-card__container cursor-pointer" onClick={handleCardClick}>
        <div className="card-body d-flex flex-column home-card__body">
          <div className="home-card__header">
       <div
  className="home-card__icon"
  style={{
    background: isTemplateCard
      ? getIconBg(agentData.category)
      : getIconBg(
          predefinedAgents?.find(
            (p) =>
              p.id === agentData.template_id ||
              (p.name &&
                agentData.type &&
                String(p.name).toLowerCase() ===
                  String(agentData.type).toLowerCase())
          )?.category
        ),
  }}
>
  <img
    src={
      isTemplateCard
        ? getCardIcon(agentData.category, false)
        : getCardIcon(
            predefinedAgents?.find(
              (p) =>
                p.id === agentData.template_id ||
                (p.name &&
                  agentData.type &&
                  String(p.name).toLowerCase() ===
                    String(agentData.type).toLowerCase())
            )?.category,
            isDraft
          )
    }
    alt="agent icon"
  />
</div>
            {!isTemplateCard && (
              <span className={`home-card__status-pill ${isDraft ? "draft" : "active"}`}>
                <span className="dot" />
                {isDraft ? "Draft" : "Active"}
              </span>
            )}
          </div>

          <div className="home-card__content">
            <h6 className="home-card__title mb-0">{title}</h6>
          
            <p className="home-card__desc">{desc}</p>
          </div>

          <hr className="home-card__divider" />

          <div className="home-card__footer">
            {isTemplateCard ? (
              <>
                <div className="home-card__count-wrap" onClick={handleTemplateCountClick}>
                  <img src="/images/newmen.svg" alt="users" />
                  <span className="home-card__count-text">{agentCount} Agents</span>
                </div>
                <div className="ms-auto">
                  {isAdded ? (
                    <button className="btn btn-success disabled">Added</button>
                  ) : (
                    canWrite && (
                      <Link
                        to="/ScreeningAgent"
                        state={{ templateName: title, templateId: keyId, templateData: agentData }}
                        className="template-add-btn"
                      >
                        Add
                      </Link>
                    )
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="home-card__model">
                  ⚙ {tags?.[0] ? tags[0].charAt(0).toUpperCase() + tags[0].slice(1) : "ChatGPT 4.0"}
                </div>
             <div className="ms-auto d-flex align-items-center gap-1 flex-nowrap">
                 <button
  style={{ border: "none", background: "transparent", padding: 0, margin: 0, boxShadow: "none", outline: "none" }}
  onClick={handleDelete}
  title="Delete Agent"
>
  <img src="/images/binn.svg" alt="Delete" style={{ width: "18px", opacity: 0.78 }} />
</button>
{isDraft ? (
  agentData.user_can_edit !== false && (
    <button
      className="home-card__configure-btn"
      onClick={() => navigate("/ScreeningAgent", { state: { existingAgent: agentData } })}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '5px'}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      Configure
    </button>
  )
) : (
  <>
    {agentData.user_can_edit !== false && (
      <button
        className="home-card__configure-btn"
        onClick={(e) => { e.stopPropagation(); navigate("/ScreeningAgent", { state: { existingAgent: agentData } }); }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '5px'}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        Update
      </button>
    )}
    <button
      className="home-card__chat-btn"
      onClick={(e) => {
        e.stopPropagation();
        if (agentData.type === "RFP Creator Agent") {
          navigate(`/chat-bot?agentId=${agentData.id}`);
        } else {
          navigate(`/ChatInterface/${agentData.id}`);
        }
      }}
    >
      Chat
    </button>
  </>
)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;